/**
 * Error thrown by `apiFetch` when a request fails because the device lost
 * connectivity, the request was aborted, or the request timed out. Distinct
 * from server/HTTP errors so callers can show a connectivity message instead
 * of the generic "technical error" — and so it can be filtered from Sentry.
 */
export class NetworkError extends Error {
  readonly cause?: unknown;

  constructor(message: string, cause?: unknown) {
    super(message);
    this.name = "NetworkError";
    this.cause = cause;
  }
}

const NETWORK_ERROR_MESSAGE_PATTERNS = [
  // Browsers / WebKit
  "failed to fetch",
  "load failed",
  "network request failed",
  "networkerror",
  // iOS NSURLErrorDomain code -1005 — surfaced via Capacitor in the device locale
  "network connection was lost",
  "the network connection was lost",
  // Danish localization of NSURLErrorDomain -1005
  "netværksforbindelsen gik tabt",
  // iOS NSURLErrorDomain -1001 — typically a request left in flight while the
  // app was suspended, failed by iOS when the app returns to the foreground
  "the request timed out",
  // Danish localization of NSURLErrorDomain -1001
  "anmodningen udløb",
  // iOS NSURLErrorDomain -1009 (NSURLErrorNotConnectedToInternet)
  "the internet connection appears to be offline",
  // Danish localization of NSURLErrorDomain -1009
  "internetforbindelsen synes at være passiv",
  // Android (java.net exceptions via CapacitorHttp): UnknownHostException
  // when DNS fails offline, ConnectException, and a SocketException when the
  // connection drops mid-request
  "unable to resolve host",
  "failed to connect to",
  "software caused connection abort",
];

// Error codes that mean the device couldn't reach the server.
// OS-PLUG-FLTR-0008 is @capacitor/file-transfer's "Failed to connect to server."
// ERR_NETWORK is axios's "Network Error" (XHR onerror, e.g. the S3 upload on web).
const NETWORK_ERROR_CODES = ["OS-PLUG-FLTR-0008", "ERR_NETWORK"];

/**
 * Capacitor plugin errors carry native details in `data`. @capacitor/file-transfer
 * puts the underlying NSError/exception text in `data.exception`, e.g. its
 * generic OS-PLUG-FLTR-0011 wraps every iOS URLError other than "not connected"
 * and "timed out" (connection lost, cancelled, DNS, TLS, ...).
 */
export const nativeErrorData = (
  error: unknown
): Record<string, unknown> | undefined => {
  if (error === null || typeof error !== "object") return undefined;
  const data = (error as Record<string, unknown>).data;
  return data !== null && typeof data === "object"
    ? (data as Record<string, unknown>)
    : undefined;
};

const nativeCause = (error: unknown): string => {
  const exception = nativeErrorData(error)?.exception;
  return typeof exception === "string" ? exception : "";
};

const matchesNetworkMessage = (...messages: string[]): boolean =>
  messages.some((message) => {
    const msg = message.toLowerCase();
    return NETWORK_ERROR_MESSAGE_PATTERNS.some((pattern) =>
      msg.includes(pattern)
    );
  });

const hasNetworkErrorCode = (error: object, message: string): boolean => {
  const code = (error as Record<string, unknown>).code;
  return NETWORK_ERROR_CODES.some(
    // toError() folds the code into the message as "<code>: <message>"
    (c) => code === c || message.startsWith(`${c}:`)
  );
};

export const isNetworkError = (error: unknown): boolean => {
  if (error instanceof NetworkError) return true;

  if (error instanceof DOMException && error.name === "AbortError") return true;

  if (error instanceof Error) {
    if (hasNetworkErrorCode(error, error.message)) return true;
    return matchesNetworkMessage(error.message, nativeCause(error));
  }

  // createAsyncThunk serializes errors to plain objects before .unwrap() re-throws them,
  // so instanceof checks above won't match. Check the serialized form directly.
  if (error !== null && typeof error === "object") {
    const e = error as Record<string, unknown>;
    if (e.name === "NetworkError") return true;
    if (e.name === "AbortError") return true;
    if (hasNetworkErrorCode(e, typeof e.message === "string" ? e.message : ""))
      return true;
    return matchesNetworkMessage(
      typeof e.message === "string" ? e.message : "",
      nativeCause(e)
    );
  }

  return false;
};
